import { useContext, useMemo } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { useGetEphemeralKey } from 'app/hooks/useStripe/useStripe';
import type { VCC_STATUS } from 'app/typings/core/common/enum/vccStatus';
import type { BookingDetails } from 'app/typings/extranetBookings/booking';
import { VccStatus } from 'app/typings/vcc';
import { formatDayOfMonth } from 'app/utils/dates';
import { getVCCStatus } from 'app/utils/vcc';

import { VirtualCard } from '../../../common/VirtualCard/VirtualCard';
import type {
  VirtualCardConfig,
  VirtualCardData,
} from '../../../common/VirtualCard/types';
import { getTooltipText } from '../../utils';

type VirtualCardProps = {
  booking: BookingDetails;
};

export const LegacyVirtualCard = ({ booking }: VirtualCardProps) => {
  const { hotelId } = useContext(HotelContext);
  const { currencyFormatter } = useContext(CurrencyContext);

  const status = getVCCStatus(booking, currencyFormatter);
  const cardIsCancelled = status.status === VccStatus.Cancelled;
  const cardIsDeactivated = status.status === VccStatus.Deactivated;
  const cardIsBlur = cardIsCancelled || cardIsDeactivated;

  const { getEphemeralKey } = useGetEphemeralKey(hotelId, booking.code);

  const data: VirtualCardData = useMemo(
    () => ({
      issuingCardId: booking.issuingCardId,
      issuingBalance: booking.issuingBalance,
      checkin: booking.checkin,
      code: booking.code,
    }),
    [
      booking.issuingCardId,
      booking.issuingBalance,
      booking.checkin,
      booking.code,
    ]
  );

  const config: VirtualCardConfig = useMemo(
    () => ({
      currencyFormatter,
      cardIsBlur,
      status,
    }),
    [currencyFormatter, cardIsBlur, status]
  );

  const stripeConfig = useMemo(
    () => ({
      getEphemeralKey,
    }),
    [getEphemeralKey]
  );

  const getTooltipTextWrapper = (statusStr: VccStatus | VCC_STATUS) => {
    const result = getTooltipText(statusStr);

    return result ?? null;
  };

  const alertConfig = useMemo(
    () => ({
      formatDate: formatDayOfMonth,
      checkinLabel: 'check-in day',
      importantMessagePart1:
        'Important: the pre-authorisation, deposit, extras or tourist tax',
      importantMessagePart2:
        "must not be made with this virtual card but with the guest's physical card.",
    }),
    []
  );

  return (
    <VirtualCard
      data={data}
      config={config}
      stripeConfig={stripeConfig}
      alertConfig={alertConfig}
      getTooltipText={getTooltipTextWrapper}
    />
  );
};
